import React, { useEffect, useState } from 'react';
import { Transaction, Order, User, SystemSettings } from '../types';
import { api } from '../services/api';
import { Button, Card } from './ui';
import { Printer, X } from 'lucide-react';

interface InvoiceViewProps {
    transaction: Transaction;
    user: User;
    onClose: () => void;
}

export const InvoiceView: React.FC<InvoiceViewProps> = ({ transaction, user, onClose }) => {
    const [order, setOrder] = useState<Order | null>(null);
    const [settings, setSettings] = useState<SystemSettings | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadData = async () => {
            try {
                const settingsData = await api.getSettings();
                setSettings(settingsData);

                if (transaction.type === 'ORDER_PAYMENT' && transaction.referenceId) {
                    // If we have API support for single order fetch by ID without token, use it.
                    // Assuming we might need to fetch full order details. 
                    // Since api.getOrders fetches all, we might want to just filter from props if available, 
                    // but fetching fresh is safer for invoice accuracy.
                    // However, api.ts currently only has getOrders() (all) or getOrderByToken().
                    // We'll use getOrders() and find it, or add getOrderById to API.
                    // For now, let's try to find it from a full fetch (inefficient but works without API change)
                    // OR better: check if we can add getOrderById to API.
                    // Actually, let's just use getOrders for now as it's cached/fast enough usually.
                    const allOrders = await api.getOrders();
                    const found = allOrders.find(o => o.id === transaction.referenceId);
                    if (found) setOrder(found);
                }
            } catch (e) {
                console.error("Error loading invoice data", e);
            } finally {
                setLoading(false);
            }
        };
        loadData();
    }, [transaction]);

    const handlePrint = () => {
        window.print();
    };

    if (loading) return <div className="p-8 text-center">Loading Invoice...</div>;

    return (
        <div className="fixed inset-0 z-[200] bg-black/80 flex items-center justify-center p-4 overflow-y-auto print:p-0 print:bg-white print:static">
            <div className="w-full max-w-3xl bg-white text-black rounded-xl shadow-2xl overflow-hidden print:shadow-none print:rounded-none">

                {/* Toolbar - Hidden in Print */}
                <div className="bg-zinc-100 p-4 border-b flex justify-between items-center print:hidden">
                    <h2 className="font-bold text-lg">Invoice Details</h2>
                    <div className="flex gap-2">
                        <Button onClick={handlePrint} variant="primary">
                            <Printer size={16} /> Print Invoice
                        </Button>
                        <Button onClick={onClose} variant="secondary">
                            <X size={16} /> Close
                        </Button>
                    </div>
                </div>

                {/* Invoice Content */}
                <div className="p-8 md:p-12 print:p-0" id="invoice-content">
                    {/* Header */}
                    <div className="flex justify-between items-start mb-12">
                        <div>
                            {settings?.logoUrl ? (
                                <img src={settings.logoUrl} alt="Lab Logo" className="h-16 object-contain mb-4" />
                            ) : (
                                <h1 className="text-3xl font-bold text-blue-900 mb-2">GALAL DENTAL LAB</h1>
                            )}
                            <div className="text-sm text-gray-500">
                                <p>Professional Dental Solutions</p>
                                <p>Cairo, Egypt</p>
                                <p>+20 155 676 1102</p>
                            </div>
                        </div>
                        <div className="text-right">
                            <h2 className="text-4xl font-light text-gray-300 uppercase tracking-widest mb-2">Invoice</h2>
                            <p className="font-mono font-bold text-lg">#{transaction.id.substring(0, 8).toUpperCase()}</p>
                            <p className="text-gray-500 text-sm mt-1">{new Date(transaction.createdAt).toLocaleDateString()}</p>
                        </div>
                    </div>

                    {/* Bill To */}
                    <div className="mb-12 border-b pb-8">
                        <h3 className="text-xs font-bold text-gray-400 uppercase mb-2">Bill To</h3>
                        <div className="text-lg font-bold">{user.name}</div>
                        <div className="text-gray-600">{user.clinicName}</div>
                        <div className="text-gray-600">{user.phone}</div>
                        <div className="text-gray-600">{user.location}</div>
                    </div>

                    {/* Transaction Details */}
                    <div className="mb-8">
                        <table className="w-full text-left">
                            <thead className="border-b-2 border-gray-100">
                                <tr>
                                    <th className="py-3 text-sm font-bold text-gray-400 uppercase">Description</th>
                                    <th className="py-3 text-right text-sm font-bold text-gray-400 uppercase">Amount</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                <tr>
                                    <td className="py-4">
                                        <div className="font-bold text-gray-800">{transaction.description}</div>
                                        <div className="text-sm text-gray-500 mt-1">
                                            Type: {transaction.type.replace('_', ' ')}
                                        </div>
                                        {order && (
                                            <div className="mt-4 bg-gray-50 p-4 rounded-lg text-sm">
                                                <p className="font-bold text-gray-700 mb-2">Case Details ({order.patientName})</p>
                                                <ul className="list-disc pl-4 space-y-1 text-gray-600">
                                                    <li>Patient: {order.patientName}</li>
                                                    <li>Tooth: {order.toothNumber}</li>
                                                    <li>Shade: {order.shade}</li>
                                                    {order.items.map((item, idx) => (
                                                        <li key={idx}>{item.serviceName} ({item.teeth.join(', ')})</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </td>
                                    <td className="py-4 text-right font-mono font-bold text-lg">
                                        {Math.abs(transaction.amount).toFixed(2)} {settings?.currency || 'EGP'}
                                    </td>
                                </tr>
                            </tbody>
                            <tfoot className="border-t-2 border-gray-100">
                                <tr>
                                    <td className="py-4 text-right font-bold text-gray-600">Total</td>
                                    <td className="py-4 text-right font-bold text-2xl text-blue-600">
                                        {Math.abs(transaction.amount).toFixed(2)} {settings?.currency || 'EGP'}
                                    </td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>

                    {/* Footer */}
                    <div className="mt-16 text-center text-xs text-gray-400">
                        <p>This is a computer-generated invoice and is valid without a signature.</p>
                        <p className="mt-2">Thank you for your business!</p>
                    </div>

                </div>
            </div>
        </div>
    );
};
